import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { APP_VERSION, APP_VERSION_LABEL } from '../version';

const DEFAULT_DESTINATION = '/agents/geologist/workspace';

interface RedirectState {
  from?: { pathname?: string; search?: string; hash?: string };
}

/** Where to land after a successful sign-in: the requested deep link, or the workspace. */
function resolveDestination(state: unknown): string {
  const from = (state as RedirectState | null)?.from;
  if (!from?.pathname || from.pathname === '/login') return DEFAULT_DESTINATION;
  return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
}

export default function Login() {
  const { status, signIn } = useAuth();
  const location = useLocation();
  const fieldPrefix = useId();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const destination = resolveDestination(location.state);

  if (status === 'authenticated') {
    return <Navigate to={destination} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
      // A successful sign-in flips the auth status; the <Navigate> above then
      // performs the redirect on the next render.
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось выполнить вход.');
      setSubmitting(false);
    }
  };

  const emailFieldId = `${fieldPrefix}-email`;
  const passwordFieldId = `${fieldPrefix}-password`;
  const errorId = `${fieldPrefix}-error`;

  return (
    <div className="login">
      <main className="login__card">
        <header className="login__header">
          <span className="login__mark" aria-hidden="true" />
          <h1 className="login__title">AI-Геолог</h1>
          <p className="login__subtitle">Доказательный анализ геологического архива</p>
        </header>

        <form className="login__form" onSubmit={(event) => void handleSubmit(event)} noValidate>
          <div className="field">
            <label className="field__label" htmlFor={emailFieldId}>
              Электронная почта
            </label>
            <input
              id={emailFieldId}
              className="field__input"
              type="email"
              name="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
              disabled={submitting}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor={passwordFieldId}>
              Пароль
            </label>
            <input
              id={passwordFieldId}
              className="field__input"
              type="password"
              name="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              disabled={submitting}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
            />
          </div>

          {error ? (
            <p className="login__error" id={errorId} role="alert">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            className="button button--primary button--block"
            disabled={submitting || email.trim().length === 0 || password.length === 0}
          >
            {submitting ? 'Выполняется вход…' : 'Войти'}
          </button>
        </form>

        <p className="login__version" title={`Версия приложения ${APP_VERSION}`}>
          {APP_VERSION_LABEL}
        </p>
      </main>
    </div>
  );
}
